import { PencilIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import Linkify from 'linkify-react';
import type { MouseEventHandler, ReactNode } from 'react';
import React, { memo, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LogoSmall } from '@/components/Logo';
import MessageEditor from '@/components/MessageEditor';
import Avatar from '@/components/UI/Avatar';
import { LongPressMenu } from '@/components/UI/LongPressMenu';
import { request } from '@/renderer/utils/api';
import { useLongPress } from '@/renderer/utils/hooks/useLongPress';
import useMe from '@/renderer/utils/useMe';

import AddReactionButton, { VALID_EMOJIS } from './AddReactionButton';
import ImageAttachment from './ImageAttachment';

// Emoji glyphs render at the surrounding text size, which reads noticeably
// small next to the message text. Wrap emoji runs in a slightly larger span so
// the glyphs sit closer to the text's cap-height, without growing the line box.
const EMOJI_SEQUENCE = /(\p{RGI_Emoji})/gv;

const renderMessageContent = (content?: string | null): ReactNode => {
  if (!content) {
    return '';
  }
  // split() with a single capture group yields text at even indices and the
  // captured emoji sequences at odd indices.
  return content.split(EMOJI_SEQUENCE).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="align-[-0.12em] text-[1.25em] leading-[0]">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const DM = ({
  children,
  receipt,
  userInfo,
}: {
  children?: ReactNode;
  receipt: API.DirectMessageReceipt;
  userInfo?: boolean;
}) => {
  const time =
    !!Date.parse(`${receipt.createdAt}`) &&
    new Date(`${receipt.createdAt}`).toLocaleTimeString('en-us');

  const { emojis } = receipt;

  const handleEmojiClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      request<{ data: { emoji: string } }>(
        { input: `/api/v1/reaction/dm/${receipt.id}`, method: 'POST' },
        {
          body: {
            data: { emoji: event.currentTarget.dataset.emoji as string },
          },
        },
      );
    },
    [receipt.id],
  );

  // Touch: long-press the message to open the reaction row as an anchored
  // popover (the touch counterpart to the desktop hover affordance) instead of
  // pinning a per-message trigger on every row (#1547 / #1470).
  const [reactionPoint, setReactionPoint] = useState<{ x: number; y: number } | null>(null);
  const longPress = useLongPress((point) => setReactionPoint(point));

  // Admin-only inline edit (#1640): admins can fix a support-chat message in
  // place, using the same editor as the Release Center. Patching the content
  // broadcasts directMessageReceipt/updated, so the edit shows live for both the
  // admin and the member. Gated on the VIEWER being an admin — a member never
  // sees the affordance, even if they open a chat URL directly.
  const me = useMe();
  const isAdmin = me?.role === 'admin';
  // Only offer edit on the viewer's OWN (outbound/sent) messages, never on a
  // message received from the other party (#1724). `userId` is the sender; a
  // message is the viewer's own when the sender is the current user. The update
  // endpoint is admin-gated, so keep the admin requirement too.
  const isOwnMessage = !!me?.id && receipt.userId === me.id;
  const canEditMessage = isAdmin && isOwnMessage;
  const [editing, setEditing] = useState(false);
  const onSaveEdit = useCallback(
    async (next: string) => {
      await request(
        { input: `/api/v1/admin/directMessage/update/${receipt.id}`, method: 'POST' },
        { body: { data: { content: next } } },
      );
    },
    [receipt.id],
  );

  const isFromAdmin =
    !!receipt.receiverUserId &&
    (receipt.user?.role === 'admin' ||
      receipt.user?.role === 'staff' ||
      receipt.receiverUserId !== receipt.userId);

  return (
    <Linkify>
      <div
        data-dmid={receipt.id}
        {...longPress}
        className={twMerge(
          'group relative flex w-full justify-start py-2 pr-4 align-top hover:bg-zinc-50 dark:hover:bg-white/5',
          // Touch: suppress the native selection/callout during a long-press.
          'no-hover:select-none no-hover:[-webkit-touch-callout:none]',
          // Highlight the row while its reaction popover is open.
          reactionPoint && 'bg-zinc-100 dark:bg-white/10',
        )}
      >
        <div className="mx-2 mt-1 flex w-14 shrink-0 flex-col items-center justify-start">
          {userInfo ? (
            <Avatar user={receipt?.user} isAdmin={isFromAdmin} />
          ) : (
            <time className="text-[9px] text-transparent group-hover:text-zinc-700 dark:group-hover:text-zinc-400">
              {time}
            </time>
          )}
        </div>
        <div className="w-4 grow">
          {userInfo && (
            <div>
              <span className="inline-flex min-w-0 items-center">
                {/* Show the sender name on every breakpoint. It was hidden below
                    md because long usernames overflowed the narrow message row
                    (#1477), which left phones with just avatar + timestamp; cap
                    the width and truncate so a long name ellipsizes instead of
                    breaking the row. Desktop keeps its roomier cap. */}
                <span
                  className={twMerge(
                    'inline-block max-w-[45vw] truncate font-bold text-sm md:max-w-sm dark:text-zinc-200',
                    isFromAdmin && 'text-emerald-800 dark:text-emerald-500',
                  )}
                >
                  {receipt?.user?.firstName} {!isFromAdmin && receipt?.user?.lastName}
                </span>
                {isFromAdmin && (
                  <LogoSmall className="ml-1.5 size-5 shrink-0 select-none md:ml-2" />
                )}
              </span>
              <time className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">{time}</time>
            </div>
          )}
          {editing ? (
            <div className="pr-4">
              <MessageEditor
                content={receipt.content ?? ''}
                onSave={onSaveEdit}
                onClose={() => setEditing(false)}
              />
            </div>
          ) : (
            <div className="space-y-3 text-[15px] leading-[1.375rem] sm:text-sm/5 dark:text-zinc-300">
              {!!receipt.content && (
                <p className="my-0 whitespace-pre-wrap break-words">
                  {renderMessageContent(receipt.content)}
                </p>
              )}
              {/* #1808 — an attached image renders below the text as a thumbnail
                  that opens the same lightbox on both staff and member views. */}
              {receipt.imageUrl && <ImageAttachment url={receipt.imageUrl} className="mt-1" />}
            </div>
          )}
          {children}

          <div
            className={twMerge(
              'flex items-center space-x-2',
              !!emojis && emojis.length > 0 && 'mt-1.5 mb-px ml-px',
            )}
          >
            {emojis?.map(({ count, emoji: nextEmoji }) => {
              const emojiChar = String.fromCodePoint(nextEmoji);

              return (
                <React.Fragment key={nextEmoji}>
                  <button
                    onClick={handleEmojiClick}
                    className="flex w-fit items-center space-x-2 rounded-full bg-emerald-500/5 px-1 ring-1 ring-emerald-500 dark:bg-emerald-400/20 dark:ring-emerald-400"
                    data-emoji={emojiChar}
                  >
                    {emojiChar}
                    <span className="font-semibold text-[10px] text-emerald-700 dark:text-emerald-400">
                      {count}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
            {!editing && (
              <div
                className={clsx(
                  // Desktop hover affordance; touch uses the long-press popover
                  // below instead of a pinned per-message trigger (#1547). One
                  // bordered, segmented row mirroring the release-center
                  // 'Edit release' toolbar (#1724): appears on hover/focus,
                  // stays hidden on touch (long-press opens the popover instead).
                  'hidden flex-row items-center divide-x rounded-sm bg-white ring-1 ring-zinc-900/10 no-hover:hidden focus-within:flex group-hover:flex dark:divide-zinc-700 dark:bg-zinc-900 dark:shadow-none dark:shadow-white/5 dark:ring-white/10',
                  emojis?.length ? '-my-1' : 'absolute right-14 top-0 z-10 -mt-4',
                )}
              >
                {/* Edit pencil (#1640) — only on the viewer's own messages
                    (#1724), as a segment beside the reaction button. */}
                {canEditMessage && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    aria-label="Edit message"
                    title="Edit message"
                    className="flex items-center px-2 py-1 text-zinc-500 hover:bg-zinc-900/5 hover:text-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-200"
                  >
                    <PencilIcon className="size-4" aria-hidden="true" />
                  </button>
                )}
                <AddReactionButton
                  isLeftAlign={!!emojis?.length}
                  onClick={handleEmojiClick}
                  embedded
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {reactionPoint && (
        <LongPressMenu point={reactionPoint} onClose={() => setReactionPoint(null)}>
          <div className="flex flex-row items-center gap-1 p-1.5">
            {VALID_EMOJIS.map((validEmoji) => (
              <button
                key={validEmoji}
                type="button"
                data-emoji={validEmoji}
                onClick={(event) => {
                  handleEmojiClick(event);
                  setReactionPoint(null);
                }}
                className="rounded-full px-2 py-1 text-xl leading-none hover:bg-zinc-900/5 dark:hover:bg-white/5"
              >
                {validEmoji}
              </button>
            ))}
            {/* Edit (#1640) — the touch counterpart to the desktop hover
                pencil, likewise only on the viewer's own messages (#1724). */}
            {canEditMessage && (
              <>
                <span
                  className="mx-0.5 h-6 w-px shrink-0 bg-zinc-900/10 dark:bg-white/10"
                  aria-hidden="true"
                />
                <button
                  type="button"
                  aria-label="Edit message"
                  title="Edit message"
                  onClick={() => {
                    setEditing(true);
                    setReactionPoint(null);
                  }}
                  className="rounded-full px-2 py-1 text-zinc-600 hover:bg-zinc-900/5 dark:text-zinc-300 dark:hover:bg-white/5"
                >
                  <PencilIcon className="size-5" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </LongPressMenu>
      )}
    </Linkify>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export default memo(
  DM,
  (prev, next) =>
    prev.receipt.content === next.receipt.content &&
    prev.receipt.imageUrl === next.receipt.imageUrl &&
    prev.receipt.visible === next.receipt.visible &&
    prev.receipt.emoji === next.receipt.emoji &&
    prev.receipt.emojis?.length === next.receipt.emojis?.length &&
    prev.receipt.emojis?.[0]?.count === next.receipt.emojis?.[0]?.count &&
    prev.receipt.emojis?.[1]?.count === next.receipt.emojis?.[1]?.count &&
    prev.receipt.emojis?.[3]?.count === next.receipt.emojis?.[3]?.count,
);
